import { useContext, useEffect } from 'react'
import { Navigate, Outlet, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { RootState } from '@/store/store'
import { AuthContext } from '@/context/auth-context'
import AnonFeatureGuard from '@/components/guards/AnonFeatureGuard'
import { FileUploadProvider } from '@/uppy-file-upload/context/FileUploadContext'
import { isPendingMatterInvite } from '@/matter/utils/is-pending-matter-invite'
import { useIsAccountCollaborationCapable } from '@/collaboration/hooks/useIsAccountCollaborationCapable'
import { CollaborationActions } from '@/collaboration/store/slice'
import { kMembersPollingIntervalMs } from '@/collaboration/constants'
import { usePollWhileVisible } from '@/hooks/usePollWhileVisible'
import { kMatterPollingIntervalMs } from '@/matter/constants'

/**
 * MatterLayout - Parent route component for matter-first URL hierarchy
 *
 * This layout sits at `/dashboard/m/:matterId` and provides matter context to all
 * child routes (chronology, billing-summaries, chat, files).
 *
 * Responsibilities:
 * - URL → Redux sync: Syncs the matterId from URL to Redux state (one-way)
 * - Validation: Redirects to matters list if the matter ID is invalid
 *
 * The URL is the source of truth for matter context:
 * - `/m/:matterId` in URL → that matter is active
 * - No `/m/:matterId` (e.g., `/dashboard/matters`) → no matter is active
 *
 * Child components should use `useParams()` to access the matterId directly.
 * Redux state is synced for sidebar and other non-route components.
 *
 * @see Phase 4 of routing refactor plan
 */
export default function MatterLayout() {
  const { matterId } = useParams<{ matterId: string }>()
  const dispatch = useAppDispatch()
  const authContext = useContext(AuthContext)
  const user = authContext?.user
  const userIsSignedIn = !!user && !user.isAnonymous
  const isAccountCollaborationCapable = useIsAccountCollaborationCapable()

  // Redux state
  const mattersById = useAppSelector((state: RootState) => state.mattersState.matters)
  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)

  // Check if the matterId from the URL is valid
  const isValidMatterId = matterId ? matterId in mattersById : false

  const matter = mattersById[matterId ?? '']
  const isPendingInvite = isPendingMatterInvite(matter)

  // Whether this matter is accessible to the current user
  const isMatterAccessible = isValidMatterId && !isPendingInvite

  // Matter and member data only need syncing on an accessible matter, and only for accounts that can
  // collaborate: without collaboration there is no one else making changes.
  const shouldSyncMatterData = isMatterAccessible && isAccountCollaborationCapable

  // A solo matter has no other members to sync. `member_count` is refreshed by the matter poll
  // below, so this turns on by itself once the matter becomes shared.
  const shouldPollMembers = shouldSyncMatterData && (matter?.member_count ?? 1) > 1

  // Simple one-way sync: URL → Redux (for sidebar and non-route components)
  // Note: activeMatterId intentionally excluded from deps - this effect only syncs URL→Redux,
  // not Redux→URL. Including it would cause the effect to "correct" user-initiated changes
  // (like clicking Home to clear matter context) before navigation completes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialLoadComplete) return
    if (!matterId || !isMatterAccessible) return

    dispatch(MattersActions.setActiveMatterId({ matterId }))
  }, [initialLoadComplete, matterId, isMatterAccessible, dispatch])

  // Open the accept invite dialog when a user navigates directly to a pending-invite matter URL.
  // The dialog is mounted at DashboardLayout so it renders after the redirect below.
  useEffect(() => {
    if (!initialLoadComplete || !isPendingInvite || !matterId) return
    dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.AcceptInvite, matterId }))
  }, [initialLoadComplete, isPendingInvite, matterId, dispatch])

  /**
   * Polls this matter so a collaborator's changes to it show up without a hard refresh.
   *
   * TODO: there's no single-matter fetch endpoint today, so we refresh the whole matter list to
   * pick up changes to the one matter being viewed. Replace this with a single-matter fetch once
   * that endpoint exists.
   */
  usePollWhileVisible(() => dispatch(MattersActions.refreshMatterList({ forceRefresh: true })), kMatterPollingIntervalMs, shouldSyncMatterData)

  /**
   * Loads this matter's members into the store on entry, so every view under the matter can resolve
   * member names without its own fetch.
   */
  useEffect(() => {
    if (!shouldSyncMatterData || !matterId) return
    dispatch(CollaborationActions.fetchMembers({ matterId }))
  }, [shouldSyncMatterData, matterId, dispatch])

  /**
   * Keeps the member list fresh while the matter is open, so membership changes show up without a
   * hard refresh.
   *
   * TODO: this is a second per-matter poll alongside the matter refresh above. A single BE endpoint
   * returning one matter with its members would collapse both into one call; adopt it together with
   * the single-matter fetch noted above once it exists.
   */
  usePollWhileVisible(
    () => {
      if (matterId) dispatch(CollaborationActions.fetchMembers({ matterId, force: true }))
    },
    kMembersPollingIntervalMs,
    shouldPollMembers
  )

  // Wait for matters to load before validating
  if (userIsSignedIn && !initialLoadComplete) {
    return null
  }

  // Redirect to matters list if the matter is inaccessible (invalid ID or unaccepted invite).
  // When the invite is pending, the useEffect above opens AcceptMatterInviteDialog on the matters list.
  if (initialLoadComplete && !isMatterAccessible) {
    return <Navigate to="/dashboard/matters" replace />
  }

  return (
    <AnonFeatureGuard showAuthOnGuard={true}>
      <FileUploadProvider>
        <Outlet />
      </FileUploadProvider>
    </AnonFeatureGuard>
  )
}
